/* eslint-disable camelcase */

import { OfferSchema } from '@b2ag/types-api-schemas'
import {
  ProduitB2AgWithoutProductsInPackApiResponse,
  ProduitCatalogueCooperativeApiResponse,
  VariantCatalogueCooperativeApiResponse,
} from '@b2ag/module-product-sdk'
import { CooperativeProduct, CooperativeVariant, FlattenedPackProduct, Product, PRODUCT_TYPES } from '../product'

import { OfferForQuota } from '../quota-config.business'

export const defaultShippingWays = {
  pickup_point: {
    title: 'Livraison relais',
    kind: 'pickup_point',
  },
  farm: {
    title: 'Livraison à la ferme',
    kind: 'farm',
  },
  click_and_collect: {
    title: 'Retrait en magasin',
    kind: 'click_and_collect',
  },
}

function mapShippingWayFromApi(shippingWay: string): { kind: string; title: string } {
  return defaultShippingWays[shippingWay] || {}
}

const mapVariantFromApi = (
  variant: VariantCatalogueCooperativeApiResponse,
  productKind: string,
  productId: string,
): CooperativeVariant => {
  const { taxes } = variant as any
  return {
    id: variant.id,
    articleReferenceSupplier: variant.article_reference_supplier,
    assortable: variant.assortable,
    ean: variant.ean,
    measure_quantity: variant.measure_quantity,
    measure_unit: variant.measure_unit,
    name: variant.name,
    type: variant.type,
    sellable: variant.sellable,
    weight: variant.weight,
    published: variant.published,
    packaging: variant.packaging,
    associated: variant.associated,
    cooperativeVariantId: variant.cooperative_variant_id,
    stockManagementType: variant.stock_management_type as 'notManaged' | 'managed',
    taxes: taxes && {
      tva: taxes.tva,
      rpd: taxes.rpd,
      rpdUnit: taxes.rpd_unit,
    },
    flowRate: variant.flow_rate,
    isSoldAsUnit: variant.is_sold_as_unit,
    associatedSeedTreatmentManagement: variant.associated_seed_treatment_management,
    precocity: variant.precocity,
    productKind,
    productId,
  }
}

function isPackProduct(product: CooperativeProduct): boolean {
  return product.type === PRODUCT_TYPES.PACK
}

function flattenPackProduct(
  packProduct: Product | CooperativeProduct,
  productsInPack: ProduitB2AgWithoutProductsInPackApiResponse[],
): FlattenedPackProduct {
  const flattenedProduct = { ...packProduct } as any as FlattenedPackProduct

  const fieldsToFlatten = [
    'crop_protection_product_formulation_name',
    'market_authorization',
    'toxicities',
    'associated_uses',
    'active_substances',
  ]

  const apiToInternalFieldMap = {
    crop_protection_product_formulation_name: 'cropProtectionProductFormulationName',
    associated_uses: 'associatedUses',
  }

  fieldsToFlatten.forEach((field) => {
    const targetField = apiToInternalFieldMap[field] || field
    flattenedProduct[targetField] = productsInPack.map((product) => {
      if (field === 'associated_uses') {
        return { id: product._id, product_name: product.name, value: product[field] }
      }
      return { product_name: product.name, value: product[field] }
    })
  })

  flattenedProduct.safetySheetUrls = productsInPack
    .filter((product) => product.documentation?.safety_sheet_urls)
    .map((product) => ({
      product_name: product.name,
      value: product.documentation!.safety_sheet_urls!,
    }))

  return flattenedProduct
}

export const mapFromApi = (product?: ProduitCatalogueCooperativeApiResponse): CooperativeProduct => {
  if (!product) return {} as CooperativeProduct
  const {
    _id,
    active_substances,
    associated_products,
    is_mention_bio_control,
    categories,
    characteristics,
    contribution_mode,
    cooperative_review,
    crops,
    description,
    valuation,
    documentation,
    family,
    crop_protection_product_formulation_name,
    generation,
    images,
    is_highlighted,
    is_restricted_explosives_precursor,
    is_supplier_highlighted,
    kind,
    market_authorization,
    composition_mix,
    mixed,
    name,
    is_mention_organic,
    origin,
    obtenteur,
    resistance,
    engrais_packaging_form,
    standard,
    stimulating_substance,
    supplier,
    supplier_logo,
    supplier_score,
    supplier_universe_page_link,
    toxicities,
    type,
    associated_uses,
    variants = [],
  } = product
  return {
    _id: _id!,
    type: type as unknown as PRODUCT_TYPES,
    active_substances,
    isMentionBioControl: is_mention_bio_control,
    supplier,
    supplierLogo: supplier_logo,
    supplierUniversePageLink: supplier_universe_page_link,
    supplierScore: supplier_score,
    cropProtectionProductFormulationName: crop_protection_product_formulation_name,
    images,
    // @ts-ignore EXPLIQUER POURQUOI CE TS IGNORE.
    isRestrictedExplosivesPrecursor: is_restricted_explosives_precursor,
    isSupplierHighlighted: is_supplier_highlighted,
    safetySheetUrls: documentation && documentation.safety_sheet_urls,
    productSheetUrls: documentation && documentation.product_sheet_urls,
    productManualUrls: documentation && documentation.product_manual_urls,
    characteristics,
    kind,
    market_authorization,
    name,
    isMentionOrganic: is_mention_organic,
    associatedUses: associated_uses,
    variants: variants.map((variant) => mapVariantFromApi(variant, kind, _id!)),
    categories,
    family,
    toxicities,
    cooperativeReview: cooperative_review,
    isHighlighted: is_highlighted,
    // @ts-ignore EXPLIQUER POURQUOI CE TS IGNORE
    associatedProducts: associated_products,
    cropName: crops,
    valuation,
    obtenteur,
    resistance,
    generation,
    compositionMix: composition_mix,
    description,
    engraisPackagingForm: engrais_packaging_form,
    origin,
    mixed,
    contributionMode: contribution_mode,
    standard,
    stimulatingSubstance: stimulating_substance,
  }
}

export const mapProductFromApiAndFlattenPackProduct = (
  responseProduct?: ProduitCatalogueCooperativeApiResponse,
): CooperativeProduct => {
  const product = mapFromApi(responseProduct)
  return isPackProduct(product) ? flattenPackProduct(product, responseProduct?.products_in_pack!) : product
}

export const mapMeasureUnit = (str: string): string => (str === 'l' ? 'L' : str)

export const mapOfferFromOfferApi = (offer: OfferSchema): OfferForQuota => ({
  offerId: offer.offer_id,
  validityEndDate: offer.validity_end_date ? new Date(offer.validity_end_date) : undefined,
  shippingStartDate: offer.shipping_start_date ? new Date(offer.shipping_start_date) : undefined,
  shippingEndDate: offer.shipping_end_date ? new Date(offer.shipping_end_date) : undefined,
  shippingMethodList: offer.shipping_method_list?.map(mapShippingWayFromApi),
  segmentList: offer.segment_list,
})
